import type { LotRow, LotValidityWarning } from '@/features/task-logging/TaskLogForm'

type DataFieldValidationSlice = {
  requiresValidation: boolean
  acceptableValues: readonly unknown[]
}

/**
 * Shared lot/supply validity warnings for Instruments Tasks and Dashboard TaskQueueCard.
 * Mirrors LotLifecycleStatus priority: discarded > expired > retired > missing selection.
 */
export function buildLotValidityWarnings({
  linkedLot,
  usedLots,
  dataFields = [],
}: {
  linkedLot: LotRow | null
  usedLots: LotRow[]
  dataFields?: DataFieldValidationSlice[]
}): LotValidityWarning[] {
  const warnings: LotValidityWarning[] = []

  if (linkedLot) {
    const name = linkedLot.name ?? linkedLot.lotNumber ?? 'Lot'
    if (linkedLot.currentLotDetailId == null) {
      warnings.push({ lotName: name, reason: 'lot_not_specified' })
    } else if (linkedLot.lifecycleStatus === 'discarded') {
      warnings.push({ lotName: name, reason: 'discarded' })
    } else if (linkedLot.lifecycleStatus === 'expired') {
      warnings.push({ lotName: name, reason: 'expired' })
    } else if (linkedLot.isLotRetired) {
      warnings.push({ lotName: name, reason: 'retired' })
    } else if (linkedLot.isLotDiscarded) {
      warnings.push({ lotName: name, reason: 'discarded' })
    } else {
      const hasValidationFields = dataFields.some((dataField) => dataField.requiresValidation)
      const allValidationFieldsEmpty = dataFields
        .filter((dataField) => dataField.requiresValidation)
        .every((dataField) => dataField.acceptableValues.length === 0)
      if (hasValidationFields && allValidationFieldsEmpty) {
        warnings.push({ lotName: name, reason: 'data_fields_missing' })
      }
    }
  }

  for (const usedLot of usedLots) {
    const name = usedLot.name ?? 'Lot'
    if (usedLot.isLotDiscarded) warnings.push({ lotName: name, reason: 'discarded' })
    else if (usedLot.currentLotDetailId == null)
      warnings.push({ lotName: name, reason: 'lot_not_specified' })
    else if (usedLot.lifecycleStatus === 'discarded')
      warnings.push({ lotName: name, reason: 'discarded' })
    else if (usedLot.lifecycleStatus === 'expired')
      warnings.push({ lotName: name, reason: 'expired' })
    else if (usedLot.isLotRetired) warnings.push({ lotName: name, reason: 'retired' })
  }

  return warnings
}
